'use client'
import type { FC } from 'react'
import type { StackProps, TextProps } from '@chakra-ui/react'
import type { ProductPrice as ProductPriceData } from 'ecosystem'
import { Price } from 'ui'
import { isNullable } from 'shared-utils'
import { useVatMode } from '../../common/hooks/useVatMode'

type ProductPriceProps = ProductPriceData & {
  originalPriceProps?: TextProps
  priceProps?: TextProps
  orientation?: 'horizontal' | 'vertical'
  prefix?: string
} & StackProps

/**
 * Smart wrapper around <Price>: resolves which side of the gross/net price pair
 * to render based on `useVatMode()` (user toggle + B2B override), then hands off
 * the chosen values to the presentational <Price>.
 *
 * Use this anywhere a price comes from the storefront product API. Use the
 * underlying <Price> in `ui` only when you already have the final numbers
 * (e.g. order summaries that store gross totals).
 */
const ProductPrice: FC<ProductPriceProps> = ({
  price,
  originalPrice,
  priceWithoutVat,
  originalPriceWithoutVat,
  ...rest
}) => {
  const { excludeVat } = useVatMode()
  // Only switch to the net side when the BE actually returned a value —
  // isNullable rejects both null AND undefined, so an unpopulated net field
  // can't accidentally render as 0 SEK.
  const useNet =
    excludeVat && (!isNullable(priceWithoutVat) || !isNullable(originalPriceWithoutVat))

  return (
    <Price
      originalPrice={useNet ? (originalPriceWithoutVat ?? null) : originalPrice}
      price={useNet ? (priceWithoutVat ?? null) : price}
      {...rest}
    />
  )
}

export default ProductPrice
